import { api } from '../../api/client';
import type { ComponentBrief } from '../components/api';
import type { LocationBrief } from '../locations/api';

export type MovementType =
  | 'receipt'
  | 'issue'
  | 'transfer_out'
  | 'transfer_in'
  | 'adjustment'
  | 'write_off'
  | 'return'
  | 'reversal';

/** Types somebody posts directly. Transfers have their own endpoint, and the
 *  two transfer halves and reversals are only ever written by the server. */
export const POSTABLE_TYPES: MovementType[] = [
  'receipt',
  'issue',
  'write_off',
  'return',
  'adjustment',
];

/** The paper a movement came from, resolved by the server: a receipt's `ref_id`
 *  names the order *line*, which is not something a screen can open or name. */
export interface DocumentRef {
  kind: 'purchase' | 'project' | 'stocktake';
  id: string;
  code: string;
  title: string | null;
}

export interface Movement {
  id: string;
  component_id: string;
  location_id: string | null;
  quantity: string;
  movement_type: MovementType;
  occurred_at: string;
  ref_type: string | null;
  ref_id: string | null;
  transfer_group_id: string | null;
  unit_cost: string | null;
  currency: string | null;
  note: string | null;
  /** The changed place: source on `transfer_out`, destination on `transfer_in`. */
  location: LocationBrief | null;
  /** Only on the history endpoint — a posting answers with what it just wrote. */
  document: DocumentRef | null;
}

export interface PriceAverage {
  currency: string;
  /** Weighted by quantity: a thousand at 0.4 and ten at 4.0 average to 0.44. */
  average: string;
  quantity: string;
  receipts: number;
}

export interface PriceHistory {
  last_unit_cost: string | null;
  last_currency: string | null;
  last_at: string | null;
  last_document: DocumentRef | null;
  /** One row per currency, never converted: the rate that made a price in yuan
   *  comparable was frozen on the order, and a ledger row carries no rate. */
  averages: PriceAverage[];
}

export interface NegativeBalance {
  component_id: string;
  location_id: string | null;
  qty: string;
}

export interface ProductionInput {
  specification_version_id: string;
  operation: 'assembly' | 'disassembly';
  quantity: string;
  source_location_id: string;
  target_location_id: string;
  note?: string | null;
}

export interface Posting {
  movements: Movement[];
  /** Balances that went below zero. Reported, never a refusal. */
  negative: NegativeBalance[];
}

export interface Placement {
  location: LocationBrief;
  qty: string;
  /** Full chain from the root, so a box name identifies a place. */
  ancestors: string[];
}

export interface ComponentReservation {
  component_id: string;
  project_id: string;
  project_code: string;
  project_name: string;
  quantity: string;
}

export interface SubtreeLine {
  component: ComponentBrief;
  qty: string;
}

export interface LowStockLine {
  component: ComponentBrief;
  qty_on_hand: string;
  min_stock: string;
  /** How many to buy to reach the minimum again — the figure an order needs. */
  shortfall: string;
  /** Who it was bought from last time: "this is running out" raises exactly one
   *  question, and this is its answer. */
  vendor_id: string | null;
  vendor_name: string | null;
}

export interface IntegrityProblem {
  component_id: string;
  location_id: string | null;
  ledger: string;
  recorded: string;
}

export interface IntegrityReport {
  ok: boolean;
  problems: IntegrityProblem[];
}

export interface MovementInput {
  component_id: string;
  location_id: string | null;
  /** Always positive: the type supplies the sign. */
  quantity: string;
  movement_type: MovementType;
  note?: string | null;
}

export interface TransferInput {
  component_id: string;
  from_location_id: string | null;
  to_location_id: string | null;
  quantity: string;
  note?: string | null;
  /** Which units moved. Required, and required to number exactly `quantity`,
   *  when the component is tracked unit by unit — the server checks it, because
   *  a transfer of three that names two leaves the ledger and the register
   *  disagreeing with no way to tell which is right. */
  serial_unit_ids?: string[];
}

export const placements = (componentId: string) =>
  api.get<Placement[]>(`/stock/components/${componentId}/placements`);

export const componentReservations = (componentId: string) =>
  api.get<ComponentReservation[]>(`/stock/components/${componentId}/reservations`);

export type PackageAvailability = 'released' | 'quarantine' | 'rejected';

export interface StockPackage {
  id: string;
  code: string;
  lot_id: string;
  lot_code: string;
  component_id: string;
  package_type: string;
  tare_weight_g: string | null;
  availability: PackageAvailability;
  note: string | null;
  location_id: string | null;
  location_name: string | null;
  quantity: string;
  external_lot_number: string | null;
  date_code: string | null;
  received_at: string | null;
  vendor_name: string | null;
  lot_note: string | null;
  is_opening_balance: boolean;
}

export const stockPackages = (componentId: string) =>
  api.get<StockPackage[]>(`/stock/components/${componentId}/packages`);

export const packageTypes = () => api.get<string[]>('/stock/package-types');
export const setPackageAvailability = (
  packageId: string,
  availability: PackageAvailability,
  note?: string,
) => api.patch<StockPackage>(`/stock/packages/${packageId}/availability`, { availability, note });
export const issuePackage = (packageId: string, quantity: string, note?: string) =>
  api.post<StockPackage>(`/stock/packages/${packageId}/issue`, { quantity, note });
export const movePackage = (packageId: string, location_id: string, note?: string) =>
  api.post<StockPackage>(`/stock/packages/${packageId}/move`, { location_id, note });
export const splitPackage = (packageId: string, quantity: string, note?: string) =>
  api.post<StockPackage>(`/stock/packages/${packageId}/split`, { quantity, note });
export const stocktakePackage = (packageId: string, counted_quantity: string, note?: string) =>
  api.post<StockPackage>(`/stock/packages/${packageId}/stocktake`, { counted_quantity, note });
export interface PackageMeasurement {
  package: StockPackage;
  counted_quantity: string;
  tare_source: 'package' | 'component' | 'package_default' | 'none';
}
export const measurePackageStocktake = (packageId: string, measured_value: string) =>
  api.post<PackageMeasurement>(`/stock/packages/${packageId}/measure-stocktake`, {
    method: 'mass', measured_value, include_carrier: true,
  });

/** Places this part has ever been in, most recent first — including ones that
 *  are empty now, which is exactly when a new parcel of it turns up. */
export const knownLocations = (componentId: string) =>
  api.get<Placement[]>(`/stock/components/${componentId}/known-locations`);

/**
 * One page of the ledger for a part.
 *
 * Asks for one row more than it means to show, and the surplus is how the caller
 * learns there is a next page: cheaper than a count, and it means "Дальше" never
 * opens a page that turns out to be empty.
 */
export const movements = (componentId: string, limit = 21, offset = 0) =>
  api.get<Movement[]>(
    `/stock/components/${componentId}/movements?limit=${limit}&offset=${offset}`,
  );

/** Behind the cost permission: what was paid is commercial information. */
export const prices = (componentId: string) =>
  api.get<PriceHistory>(`/stock/components/${componentId}/prices`);

export const postMovement = (input: MovementInput) =>
  api.post<Posting>('/stock/movements', input);

export const transfer = (input: TransferInput) => api.post<Posting>('/stock/transfers', input);

export const postProduction = (input: ProductionInput) =>
  api.post<Posting>('/stock/productions', input);

export const reverseMovement = (movementId: string, note?: string) =>
  api.post<Posting>(
    `/stock/movements/${movementId}/reverse${note ? `?note=${encodeURIComponent(note)}` : ''}`,
  );

export const locationContents = (locationId: string) =>
  api.get<SubtreeLine[]>(`/stock/locations/${locationId}/contents`);

export interface LocationReportPlace {
  location_id: string;
  location_name: string;
  location_path: string;
}

export interface LocationReportLine {
  component_id: string;
  component_code: string;
  component_name: string;
  track_serials: boolean;
  location_id: string;
  location_name: string;
  qty: string;
}

export interface LocationReport {
  location_id: string;
  include_descendants: boolean;
  locations: LocationReportPlace[];
  lines: LocationReportLine[];
}

export const locationReport = (locationId: string, includeDescendants: boolean) =>
  api.get<LocationReport>(
    `/stock/location-report?location_id=${encodeURIComponent(locationId)}&include_descendants=${includeDescendants}`,
  );

export const lowStock = () => api.get<LowStockLine[]>('/stock/low');

export const checkIntegrity = () => api.get<IntegrityReport>('/stock/integrity');

export type StocktakeStatus = 'open' | 'posted' | 'cancelled';

export interface StocktakeLine {
  id: string;
  component_id: string;
  location_id: string;
  expected_qty: string;
  counted_qty: string | null;
  note: string | null;
  measurement: {
    method: 'mass' | 'length';
    measured_value: string;
    unit: 'g' | 'mm';
    profile_source: 'component' | 'package';
  } | null;
  component_code: string | null;
  component_name: string | null;
  track_serials: boolean;
  location_name: string | null;
  changed_during_count: boolean;
}

export interface StocktakeLocation {
  location_id: string;
  location_name: string | null;
  location_path: string | null;
  status: 'pending' | 'completed';
  completed_at: string | null;
}

export interface StocktakeSerial {
  serial_unit_id: string;
  code: string;
  serial: string | null;
  component_id: string;
  component_code: string;
  component_name: string;
  expected_location_id: string | null;
  found_location_id: string | null;
}

export interface Stocktake {
  id: string;
  code: string;
  location_id: string | null;
  include_descendants: boolean;
  status: StocktakeStatus;
  note: string | null;
  posted_at: string | null;
  created_at: string;
  lines: StocktakeLine[];
  locations: StocktakeLocation[];
  serials: StocktakeSerial[];
}

export type StocktakeSummary = Omit<Stocktake, 'lines' | 'locations' | 'serials'> & {
  location_count: number;
  line_count: number;
  counted_count: number;
};

export const listStocktakes = () => api.get<StocktakeSummary[]>('/stock/stocktakes');
export const getStocktake = (id: string) => api.get<Stocktake>(`/stock/stocktakes/${id}`);
export const openStocktake = (input: {
  location_id: string | null;
  include_descendants: boolean;
  note?: string | null;
}) => api.post<Stocktake>('/stock/stocktakes', input);
export const countStocktake = (
  id: string,
  input: { component_id: string; location_id: string; counted_qty: string; note?: string | null },
) => api.post<StocktakeLine>(`/stock/stocktakes/${id}/count`, input);
export const measureStocktake = (
  id: string,
  input: { component_id: string; location_id: string; method: 'mass' | 'length'; measured_value: string; include_carrier: boolean },
) => api.post<StocktakeLine>(`/stock/stocktakes/${id}/measure`, input);
export const setStocktakeLocation = (id: string, locationId: string, complete: boolean) =>
  api.patch<StocktakeLocation>(`/stock/stocktakes/${id}/locations/${locationId}`, { complete });
export const observeStocktakeSerial = (
  id: string,
  input: { location_id: string; serial_unit_id?: string; component_id?: string; serial?: string },
) =>
  api.post<Stocktake>(`/stock/stocktakes/${id}/serials`, {
    ...input,
  });
export const postStocktake = (id: string, confirmChanged = false) =>
  api.post<{ session: Stocktake; adjusted: number; unchanged: number; skipped: number }>(
    `/stock/stocktakes/${id}/post`,
    { confirm_changed: confirmChanged },
  );
export const cancelStocktake = (id: string) =>
  api.post<Stocktake>(`/stock/stocktakes/${id}/cancel`);

/** One part every running project together is short of.
 *
 *  Not the sum of each project's own shortage: a part on the shelf can only be
 *  fitted once, so stock is taken off the total demand rather than off each
 *  project's separately — see `project_service.shortages`. */
export interface ShortageRow {
  component_id: string;
  internal_code: string;
  name: string;
  mpn: string | null;
  unit_code: string | null;
  required: string;
  issued: string;
  on_hand: string;
  ordered: string;
  draft_quantity: string;
  shortage: string;
  /** Codes of the projects asking for it. */
  projects: string[];
  vendor_id: string | null;
  vendor_name: string | null;
}

export const shortages = () => api.get<ShortageRow[]>('/projects/shortages');

/** The same tables as files. Written by the server, which already owns the one
 *  spreadsheet writer this application has. */
export const lowStockXlsx = () => api.blob('/stock/low.xlsx');
export const shortagesXlsx = () => api.blob('/projects/shortages.xlsx');
